'use client';

import {SectionHeader} from '@/components/main/common';
import {MAIN_LIST_CNT} from '@/constants/main';
import {getCookie, getTimeStamp, isLogin} from '@/utils/main';
import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import useMainStore from '@/stores/main';
import LiveBroadcastItem from '../LiveBroadcastItem';
import {MY_PLUS_LIVE_CONTEXT_MENU, LIVE_CONTEXT_MENU} from '@/constants/main';
import {SoopLoadingSpinner} from '@/components/ui';
import useListViewLog from '../hooks/useListViewLog';
import {cookie} from '@afreecatv/domains';
import {BROADCAST_TYPES} from '@/constants';
import {getDomParser} from '@/utils/main/my-plus/section';

export const LiveGroup = ({response, selectedMode, isLoading, order}) => {
  const navigate = useNavigate();
  const [visibleListCount, setVisibleListCount] = useState(0);
  const [isClickMore, setIsClickMore] = useState(false);
  const [headerText, setHeaderText] = useState('선호 LIVE');
  const {resizeMainLiveCount} = useMainStore();
  const userId = getCookie(cookie.BBS_COOKIE_NAME);
  const uuid = getCookie('_au');
  const sessionId = uuid || userId;

  //LIVE_LIST_VIEW hook
  const {startObserve, endObserve, updateListViewParam, updateTargetItemList} =
    useListViewLog(
      {
        path1: 'myplus',
        path2: 'live',
        listType: 'LIVE',
        selector: '#prefer_list',
        threshold: 0.0,
      },
      [],
    );

  //선호 live_listview 에 대해서 스크롤이 멈추는 영역마다 노출된 리스트에 대해서만 sendLog 처리를 해달라고해서 자연스럽게 로직이 복잡해짐
  //스크롤이 발생할때마다 로그를 전송하면 로그가 수없이 전송될거같아서 디바운스 처리르하여 스크롤 후, 스크롤이 멈춤시점으로부터 0.3초 뒤에 sendLog
  //set 자료형을 사용하여 한번 전송된 리스트는 전송안되도록 처리
  const loggedBroadcastIds = useRef(new Set());
  const isSendingLog = useRef(false);
  const scrollTimeout = useRef(null);
  useEffect(() => {
    if (selectedMode !== 'prefer' || !isLogin() || !isClickMore) return;
    const listContainer = document.getElementById('prefer_list');
    if (!listContainer) return;

    const sendVisibleItems = () => {
      if (isSendingLog.current) return;
      isSendingLog.current = true;

      const visibleIndexes = [];
      response.liveList.forEach((item, index) => {
        const el = listContainer.querySelector(
          `[data-broadcast-id="${item.broadNo}"]`,
        );
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const windowHeight =
          window.innerHeight || document.documentElement.clientHeight;
        const visibleHeight =
          Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
        const ratio =
          rect.height > 0 ? Math.max(0, visibleHeight / rect.height) : 0;

        if (ratio >= 0.5) {
          visibleIndexes.push(index);
        }
      });

      if (visibleIndexes.length === 0) {
        isSendingLog.current = false;
        return;
      }

      const startIndex = loggedBroadcastIds.current.size;
      const endIndex = Math.max(...visibleIndexes);
      const slicedList = response.liveList.slice(startIndex, endIndex + 1);
      const newVisibleItems = slicedList.filter(
        (item) => !loggedBroadcastIds.current.has(item.broadNo),
      );
      if (newVisibleItems.length === 0) {
        isSendingLog.current = false;
        return;
      }

      newVisibleItems.forEach((item) => {
        loggedBroadcastIds.current.add(item.broadNo);
      });

      updateListViewParam({
        ...(isClickMore && {path3: 'expand'}),
        listViewCliSession: `myplus_${sessionId}_${getTimeStamp()}`,
        listViewSession: response?.liveList[0]?.listViewSession,
      });

      updateTargetItemList(
        newVisibleItems.map((item) => ({
          broadcastId: item.broadNo,
          listDataType: item.listDataType,
        })),
      );

      startObserve().finally(() => {
        isSendingLog.current = false;
      });
    };

    const handleScroll = () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      scrollTimeout.current = setTimeout(() => {
        sendVisibleItems();
      }, 300);
    };

    // 초기 진입 또는 더보기 클릭 시 최초 전송
    sendVisibleItems();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [selectedMode, isClickMore]);

  useEffect(() => {
    if (
      selectedMode === 'prefer' &&
      isLogin() &&
      response?.liveList?.length > 0
    ) {
      // slice 범위만 조건에 따라 분기
      const slicedList = response.liveList.slice(0, visibleListCount);

      slicedList.forEach((item) => {
        loggedBroadcastIds.current.add(item.broadNo);
      });

      updateListViewParam({
        listViewCliSession: `myplus_${sessionId}_${getTimeStamp()}`,
        listViewSession: response.liveList[0].listViewSession,
      });

      updateTargetItemList(
        slicedList.map((item) => ({
          broadcastId: item.broadNo,
          listDataType: item.listDataType,
        })),
      );

      startObserve();
      return () => endObserve();
    }
  }, [response]);

  useEffect(() => {
    if (selectedMode === 'prefer' && isLogin()) {
      setHeaderText('선호 LIVE');
    }
  }, [selectedMode]);

  useEffect(() => {
    if (resizeMainLiveCount > 0 && !isClickMore) {
      setVisibleListCount(resizeMainLiveCount * 2);
    }
  }, [resizeMainLiveCount]);

  const handleShowMore = () => {
    if (visibleListCount < MAIN_LIST_CNT) {
      const visibleCount = MAIN_LIST_CNT - visibleListCount;
      setVisibleListCount((prev) => prev + visibleCount);
    } else {
      const visibleCount = response.liveCnt - visibleListCount;
      setVisibleListCount((prev) => prev + visibleCount);
    }

    setIsClickMore(true);
  };

  return (
    <>
      <SectionHeader title={headerText} />
      {isLoading ? (
        <SoopLoadingSpinner />
      ) : (
        <>
          {response.liveList && response.liveList.length > 0 ? (
            <div className={`cBox-list`} id={'prefer_list'}>
              <ul>
                {response?.liveList?.map(
                  (broadcast, idx) =>
                    idx < visibleListCount && (
                      <li
                        data-type='cBox'
                        data-broadcast-id={broadcast.broadNo}
                        key={broadcast.broadNo}>
                        <LiveBroadcastItem
                          broadcastType={BROADCAST_TYPES.LIVE}
                          location='myplus'
                          broadcastId={broadcast.broadNo}
                          thumbnailUrl={broadcast.broadThumb}
                          userId={broadcast.userId}
                          userNickname={broadcast.userNick}
                          viewCount={broadcast.totalViewCnt}
                          liveType={broadcast.broadType}
                          title={broadcast.broadTitle}
                          categoryName={broadcast.categoryName}
                          categoryNo={broadcast.broadCateNo}
                          categoryTags={broadcast.categoryTags}
                          autoHashTags={broadcast.autoHashtags}
                          hashTags={broadcast.hashTags}
                          canResend={Boolean(Number(broadcast.visitBroadType))}
                          allowsAdult={Number(broadcast.broadGrade) === 19}
                          listDataType={broadcast.listDataType}
                          score={broadcast.score}
                          startDateTime={broadcast.broadStart}
                          hasDrops={Boolean(broadcast.isDrops)}
                          hasPassword={broadcast.isPassword === 'Y'}
                          contextMenu={{
                            menuList: MY_PLUS_LIVE_CONTEXT_MENU,
                          }}
                          log={{
                            data: {
                              path3: 'live',
                              listViewCliSession: `myplus_${sessionId}_${getTimeStamp()}`,
                              listViewSession: broadcast.listViewSession,
                              sectionIdx: order,
                              sectionTitle: getDomParser(headerText),
                            },
                            listDataType: broadcast.listDataType,
                          }}
                          subscriptionOnly={broadcast.subscriptionOnly}
                        />
                      </li>
                    ),
                )}
              </ul>
            </div>
          ) : (
            // response 값이 있고 라이브 목록이 없을 때 표시
            <div className='nt_area'>
              <strong>현재 방송 중인 선호 스트리머가 없습니다.</strong>
              <p>
                LIVE 참여, 즐겨찾기 등 다양한 활동을 통해 나에게 맞는 스트리머를
                찾아보세요
              </p>
            </div>
          )}
        </>
      )}
      {response?.liveCnt > visibleListCount ? (
        <div
          className={`show_more ${selectedMode === 'prefer' && isLogin() ? '' : 'all'}`}>
          <button type='button' onClick={handleShowMore}>
            더보기
          </button>
        </div>
      ) : (
        <i className={'divider'} />
      )}
    </>
  );
};
